import { ref, watch, type Ref } from "vue";
import { systemVarsPool } from "@/utils/const";
import { extractInfoNodesData } from "@/flow/flow-util/node/utils";
import { t } from "@/utils/i18n";
import { JudgeType, Variable } from "@/types/conditionJudgeNodeType";

export const useEndReplyVariables = (
  xGraph: Ref<any> | undefined,
  varIdList: Ref<Array<{ id: string; nodeId: string; varName: string }>>,
) => {
  const variables = ref<Variable[]>([]);

  watch(
    xGraph as any,
    (newVal) => {
      if (!newVal?.getNodes) {
        variables.value = [];
        return;
      }

      const systemVars: Variable[] = systemVarsPool.map((v) => ({
        value: `\${${JudgeType.SYSTEM}-${v.value}}`,
        label: v.label,
        type: JudgeType.SYSTEM,
      }));

      const nodes = newVal.getNodes();
      const infoVars: Variable[] = extractInfoNodesData(nodes)
        .map((v) => {
          const validChildren = v.infos
            .filter((info) => info.name && info.name.trim())
            .map((info) => ({
              value: `\${${JudgeType.INFO}-${info.id}}`,
              label: info.name,
              type: JudgeType.INFO,
              id: info.id,
            }));

          return {
            value: v.nodeId,
            type: JudgeType.INFO,
            label: v.nodeName,
            id: v.nodeId,
            children: validChildren,
          };
        })
        .filter((v) => (v.children?.length ?? 0) > 0);

      const apiCallNodesData: Array<{
        nodeId: string;
        nodeName: string;
        returns: Array<{ variable_id: string; key: string }>;
      }> = [];

      nodes.forEach((node) => {
        const nodeModel = node.getModel ? node.getModel() : node;
        const nodeData = nodeModel.nodeData;
        if (
          nodeData.nodeType === "interfaceInvocationNode" &&
          nodeData.returns &&
          Array.isArray(nodeData.returns)
        ) {
          apiCallNodesData.push({
            nodeId: nodeModel.id || nodeData.id,
            nodeName: nodeData.nodeName || "",
            returns: nodeData.returns.filter(
              (ret) =>
                ret.variable_id &&
                ret.key &&
                ret.variable_id.startsWith("userdefine-"),
            ),
          });
        }
      });

      const apiCallVars: Variable[] = apiCallNodesData
        .map((node) => ({
          value: node.nodeId,
          type: JudgeType.CUSTOM,
          label: node.nodeName,
          id: node.nodeId,
          children: node.returns.map((ret) => {
            const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
            return {
              value: `\${${JudgeType.CUSTOM}-${ret.variable_id}}`,
              label: varInfo ? varInfo.varName : ret.key,
              type: JudgeType.CUSTOM,
              id: ret.variable_id,
            };
          }),
        }))
        .filter((v) => (v.children?.length ?? 0) > 0);

      variables.value = [
        {
          value: JudgeType.SYSTEM,
          label: t("系统变量"),
          type: JudgeType.SYSTEM,
          children: systemVars,
        },
        ...infoVars,
        ...apiCallVars,
      ];
    },
    { immediate: true },
  );

  return {
    variables,
  };
};
